import { Command } from "commander";

import { type LlmComposition } from "../llm/composition.js";
import { commandIngest } from "../usecases/ingest/command.js";
import { commandIngestStatus } from "../usecases/ingest/status.js";
import { commandSkipVideoEpisode } from "../usecases/ingest/skip-video-episode.js";
import { commandGenerateVideoStateEvidence } from "../usecases/ingest/generate-video-state-evidence.js";
import { commandView } from "../usecases/ingest/view-command.js";
import { commandAuditAssets } from "../usecases/script/audit-assets.js";
import { commandCreate } from "../usecases/script/create.js";
import { commandMigrate } from "../usecases/script/migrate.js";
import { commandSynopsisGenerate } from "../usecases/script/synopsis-generate.js";
import {
  commandCheckout,
  commandWorkingCopyPublish,
  commandWorkingCopyStatus,
} from "../usecases/script/working-copy.js";
import {
  describeArguments,
  dispatch,
  type RunState,
  withScriptOpts,
  withWorkingCopyOpts,
} from "./registry.js";

export function registerWorkflowCommands(program: Command, state: RunState, llm: LlmComposition): void {
  program.command("migrate")
    .description("Create a newer Script schema copy through the registered migration chain")
    .requiredOption("-p, --script-path <path>", "Source Script JSON file")
    .requiredOption("-o, --output <file>", "Destination Script JSON file")
    .requiredOption("--to <version>", "Target Script version")
    .option("--manifest <file>", "Migration manifest output")
    .option("--force", "Replace existing migration artifacts")
    .addHelpText(
      "after",
      "\nExample:\n  scriptctl migrate -p ./old-script.json -o ./script-v4.json --to 4\n",
    )
    .action(dispatch(commandMigrate, state));

  const ingest = program.command("ingest")
    .description("Convert source files into a Script v4 workspace")
    .option("-s, --source-path <path>", "Source file or directory")
    .option("-w, --workspace-path <path>", "Workspace directory", "workspace")
    .option("--llm-config <path>", "LLM configuration file")
    .option("--concurrency <n>", "Maximum concurrent model calls")
    .option("--fps <n>", "Video sampling rate")
    .option("--upload-mode <mode>", "Video transport: file-api (default) or base64")
    .option("--video-provider <provider>", "Video provider: gemini or doubao (explicit selection disables fallback)")
    .option("--provider-base-url <url>", "Selected video provider service root")
    .option("--provider-api-key <key>", "Provider API key (visible in process arguments; prefer --provider-api-key-env)")
    .option("--provider-api-key-env <name>", "Environment variable containing the provider API key")
    .option("--provider-model <model>", "Selected video provider model (Doubao defaults to ARK_MODEL)")
    .option("--step", "Process only the next unfinished video episode")
    .option("--force", "Rebuild completed stages")
    .addHelpText("after", "\nExample:\n  scriptctl ingest --source-path ./draft.md --workspace-path ./workspace\n")
    .action(dispatch((options) => commandIngest(options, llm), state));

  ingest.command("status")
    .description("Show ingest workspace progress")
    .option("-w, --workspace-path <path>", "Workspace directory")
    .action(dispatch(commandIngestStatus, state));

  ingest.command("skip")
    .description("Manually accept the next failed video episode as empty")
    .option("-w, --workspace-path <path>", "Workspace directory")
    .requiredOption("--episode <n>", "Failed episode number")
    .requiredOption("--reason <text>", "Why this episode is being skipped")
    .addHelpText("after", "\nExample:\n  scriptctl ingest skip -w ./workspace --episode 7 --reason \"provider policy block\"\n")
    .action(dispatch(commandSkipVideoEpisode, state));

  program.command("evidence")
    .description("Generate local original-video reference frames from final Script v4 timestamps")
    .option("-p, --script-path <path>", "Final Script v4 JSON file")
    .requiredOption("-s, --source-path <path>", "Linked source-video file or episode directory")
    .requiredOption("-w, --workspace-path <path>", "Project workspace directory")
    .option("--force", "Re-extract frames even when deterministic output files already exist")
    .addHelpText("after", "\nExample:\n  scriptctl evidence -p ./script.json -s /Volumes/NAS/videos -w ./workspace --force\n")
    .action(dispatch(commandGenerateVideoStateEvidence, state));

  program.command("view")
    .description("Render or serve the workspace review page")
    .option("-w, --workspace-path <path>", "Workspace directory", "workspace")
    .option("--watch", "Serve and refresh the review page")
    .option("--port <n>", "HTTP server port")
    .action(dispatch(commandView, state));

  withWorkingCopyOpts(program.command("checkout"))
    .description("Download a remote script into a managed local copy")
    .option("--discard-local", "Replace an existing dirty local copy")
    .action(dispatch(commandCheckout, state));

  withScriptOpts(program.command("status"), "read")
    .description("Show local working-copy state")
    .action(dispatch(commandWorkingCopyStatus, state));

  withWorkingCopyOpts(program.command("publish"))
    .description("Validate and publish the local working copy")
    .action(dispatch(commandWorkingCopyPublish, state));

  // The .scriptctl authoring surface (write / compose / export) is gone with
  // the rest of the old agent-facing CLI: `read` emits the prose that the new
  // `write` accepts back, so an agent needs neither a bracket grammar nor a
  // plan digest. Ingest, checkout, publish and view stay — they are pipeline
  // steps, not tools an agent reaches for.

  // These three used to sit among the agent commands, but they are pipeline
  // steps too: `create` and `synopsis generate` are called by redraw's source
  // preparation workflow, and `audit-assets` is a whole-script LLM pass, not
  // something an agent reaches for mid-review.
  withScriptOpts(program.command("create"))
    .description("Create an empty script")
    .option("--title <title>", "Script title")
    .option("--force", "Replace an existing script file")
    .action(dispatch(commandCreate, state));

  withScriptOpts(program.command("audit-assets"), "read")
    .description("Audit asset consistency with an LLM")
    .option("--llm-config <path>", "LLM configuration file")
    .option("-o, --output <path>", "Audit JSON output file")
    .action(dispatch((options) => commandAuditAssets(options, llm), state));

  // Setting synopsis text is `edit --synopsis`; this parent only carries the
  // LLM generator, which redraw invokes as `synopsis generate`.
  const synopsis = program.command("synopsis")
    .description("Generate synopsis fields with an LLM")
    .action(() => synopsis.outputHelp());

  withScriptOpts(synopsis.command("generate"))
    .description("Generate synopsis fields with an LLM")
    .option("--llm-config <path>", "LLM configuration file")
    .option("--concurrency <n>", "Maximum concurrent model calls", "30")
    .option("--checkpoint <dir>", "Checkpoint directory")
    .option("--overwrite", "Regenerate existing synopsis fields")
    .option("--apply", "Write generated fields")
    .option("-n, --dry-run", "Generate a preview without writing")
    .action(dispatch((options) => commandSynopsisGenerate(options, llm), state));
}
